import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileMedical, faPlus, faLock, faEye, faStethoscope } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import ProviderShell, { useProviderApi } from './layouts/ProviderShell';
import { getSpecialtyExtraFields, SpecialtyField, SPECIALTY_FIELD_LABELS } from './specialtyFields';

interface RecordSummary {
    id: number;
    patient: { patient_id: string; name: string };
    visit_date: string;
    chief_complaint: string;
    diagnosis: string;
    status: 'draft' | 'finalized';
}

interface RecordDetail extends RecordSummary {
    history_of_present_illness: string;
    physical_examination: string;
    assessment: string;
    plan: string;
    medications_prescribed: string;
    follow_up_instructions: string;
    vitals: Record<string, any>;
}

interface PatientOption {
    patient_id: string;
    first_name: string;
    last_name: string;
}

const emptyForm = {
    patient_id: '', visit_date: '', chief_complaint: '', history_of_present_illness: '',
    physical_examination: '', assessment: '', diagnosis: '', plan: '',
    medications_prescribed: '', follow_up_instructions: '',
    bp: '', hr: '', temp: '', weight_kg: '',
};

const ProviderRecords: React.FC = () => {
    const { api } = useProviderApi();

    const [records, setRecords] = useState<RecordSummary[]>([]);
    const [patients, setPatients] = useState<PatientOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [patientFilter, setPatientFilter] = useState('');

    const [showCreate, setShowCreate] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [submitting, setSubmitting] = useState(false);

    const [viewing, setViewing] = useState<RecordDetail | null>(null);

    // Specialty-contextual exam fields (tracker 9.9) - which extra
    // structured findings to show on this form depends on the logged-in
    // provider's own specialization, fetched once from their profile.
    const [specialization, setSpecialization] = useState<string | null>(null);
    const [extraFields, setExtraFields] = useState<Record<string, string>>({});
    const specialtyFieldDefs: SpecialtyField[] = getSpecialtyExtraFields(specialization);

    useEffect(() => { document.title = 'Clinical Records - Uzima Healthcare'; }, []);

    useEffect(() => {
        api.get('practitioners/me/').then(res => {
            if (res.data?.status === 'success') setSpecialization(res.data.data?.specialization || null);
        }).catch(() => { /* form still works without specialty-specific fields */ });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadRecords = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (patientFilter) params.set('patient_id', patientFilter);
            const res = await api.get(`practitioners/medical-records/?${params}`);
            if (res.data?.status === 'success') setRecords(res.data.data || []);
        } catch { setRecords([]); } finally { setLoading(false); }
    };

    const loadPatients = async () => {
        try {
            const res = await api.get('practitioners/patients/?status=active');
            if (res.data?.status === 'success') setPatients(res.data.data.map((p: any) => ({ patient_id: p.patient_id, first_name: p.first_name, last_name: p.last_name })));
        } catch { /* patient select stays empty on failure */ }
    };

    useEffect(() => { loadRecords(); loadPatients(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { loadRecords(); }, [patientFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    const submitRecord = async () => {
        if (!form.patient_id || !form.visit_date || !form.chief_complaint.trim() || !form.assessment.trim() || !form.plan.trim()) {
            Swal.fire({ icon: 'warning', title: 'Missing Fields', text: 'Patient, visit date, chief complaint, assessment and plan are required.' });
            return;
        }
        setSubmitting(true);
        try {
            const vitals: Record<string, any> = {};
            if (form.bp) vitals.bp = form.bp;
            if (form.hr) vitals.hr = form.hr;
            if (form.temp) vitals.temp = form.temp;
            if (form.weight_kg) vitals.weight_kg = form.weight_kg;
            for (const field of specialtyFieldDefs) {
                if (extraFields[field.key]) vitals[field.key] = extraFields[field.key];
            }

            const res = await api.post('practitioners/medical-records/', { ...form, vitals });
            if (res.data?.status === 'success') {
                Swal.fire({ icon: 'success', title: 'Record Created', timer: 1500, showConfirmButton: false });
                setShowCreate(false);
                setForm(emptyForm);
                setExtraFields({});
                loadRecords();
            }
        } catch (err: any) {
            Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.message || 'Failed to create record.' });
        } finally {
            setSubmitting(false);
        }
    };

    const openRecord = async (r: RecordSummary) => {
        try {
            const res = await api.get(`practitioners/medical-records/${r.id}/`);
            if (res.data?.status === 'success') setViewing(res.data.data);
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to load record.' });
        }
    };

    const finalizeRecord = async () => {
        if (!viewing) return;
        const confirm = await Swal.fire({ title: 'Finalize this record?', text: 'It cannot be edited after finalizing.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#0f766e' });
        if (!confirm.isConfirmed) return;
        try {
            const res = await api.patch(`practitioners/medical-records/${viewing.id}/`, { status: 'finalized' });
            if (res.data?.status === 'success') {
                setViewing(res.data.data);
                loadRecords();
            }
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to finalize record.' });
        }
    };

    return (
        <ProviderShell>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faFileMedical} className="me-2 text-primary" />Clinical Records</h2>
                    <p className="text-muted mb-0">Visit notes for your patients</p>
                </div>
                <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
                    <FontAwesomeIcon icon={faPlus} className="me-2" />New Record
                </button>
            </div>

            <div className="mb-3">
                <select className="form-select" style={{ maxWidth: 260 }} value={patientFilter} onChange={(e) => setPatientFilter(e.target.value)}>
                    <option value="">All Patients</option>
                    {patients.map(p => <option key={p.patient_id} value={p.patient_id}>{p.first_name} {p.last_name}</option>)}
                </select>
            </div>

            <div className="card">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : records.length === 0 ? (
                        <div className="text-center py-5">
                            <FontAwesomeIcon icon={faFileMedical} className="text-muted mb-3" style={{ fontSize: '2.5rem' }} />
                            <h5>No clinical records yet</h5>
                            <p className="text-muted">Create a visit note for a patient on your roster.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr><th>Patient</th><th>Visit Date</th><th>Chief Complaint</th><th>Diagnosis</th><th>Status</th><th></th></tr>
                                </thead>
                                <tbody>
                                    {records.map(r => (
                                        <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => openRecord(r)}>
                                            <td>{r.patient.name}</td>
                                            <td>{new Date(r.visit_date).toLocaleString()}</td>
                                            <td>{r.chief_complaint}</td>
                                            <td>{r.diagnosis || '—'}</td>
                                            <td>
                                                <span className={`badge ${r.status === 'finalized' ? 'bg-success' : 'bg-warning'}`}>
                                                    {r.status === 'finalized' && <FontAwesomeIcon icon={faLock} className="me-1" />}
                                                    {r.status}
                                                </span>
                                            </td>
                                            <td><button className="btn btn-sm btn-outline-primary" onClick={(e) => { e.stopPropagation(); openRecord(r); }}><FontAwesomeIcon icon={faEye} /></button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {showCreate && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">New Clinical Record</h5>
                                <button type="button" className="btn-close" onClick={() => setShowCreate(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row g-3 mb-3">
                                    <div className="col-md-6">
                                        <label className="form-label">Patient</label>
                                        <select className="form-select" value={form.patient_id} onChange={(e) => setForm(f => ({ ...f, patient_id: e.target.value }))}>
                                            <option value="">Select a patient...</option>
                                            {patients.map(p => <option key={p.patient_id} value={p.patient_id}>{p.first_name} {p.last_name}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Visit Date</label>
                                        <input type="datetime-local" className="form-control" value={form.visit_date} onChange={(e) => setForm(f => ({ ...f, visit_date: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Chief Complaint</label>
                                    <textarea className="form-control" rows={2} value={form.chief_complaint} onChange={(e) => setForm(f => ({ ...f, chief_complaint: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">History of Present Illness</label>
                                    <textarea className="form-control" rows={2} value={form.history_of_present_illness} onChange={(e) => setForm(f => ({ ...f, history_of_present_illness: e.target.value }))} />
                                </div>

                                <label className="form-label">Vitals</label>
                                <div className="row g-3 mb-3">
                                    <div className="col-md-3"><input type="text" className="form-control" placeholder="BP e.g. 120/80" value={form.bp} onChange={(e) => setForm(f => ({ ...f, bp: e.target.value }))} /></div>
                                    <div className="col-md-3"><input type="text" className="form-control" placeholder="HR (bpm)" value={form.hr} onChange={(e) => setForm(f => ({ ...f, hr: e.target.value }))} /></div>
                                    <div className="col-md-3"><input type="text" className="form-control" placeholder="Temp (°C)" value={form.temp} onChange={(e) => setForm(f => ({ ...f, temp: e.target.value }))} /></div>
                                    <div className="col-md-3"><input type="text" className="form-control" placeholder="Weight (kg)" value={form.weight_kg} onChange={(e) => setForm(f => ({ ...f, weight_kg: e.target.value }))} /></div>
                                </div>

                                {specialtyFieldDefs.length > 0 && (
                                    <div className="mb-3">
                                        <label className="form-label"><FontAwesomeIcon icon={faStethoscope} className="me-2 text-primary" />{specialization} Findings</label>
                                        <div className="row g-3">
                                            {specialtyFieldDefs.map(field => (
                                                <div className="col-md-4" key={field.key}>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder={field.placeholder || field.label}
                                                        title={field.label}
                                                        value={extraFields[field.key] || ''}
                                                        onChange={(e) => setExtraFields(f => ({ ...f, [field.key]: e.target.value }))}
                                                    />
                                                    <div className="form-text">{field.label}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label className="form-label">Physical Examination</label>
                                    <textarea className="form-control" rows={2} value={form.physical_examination} onChange={(e) => setForm(f => ({ ...f, physical_examination: e.target.value }))} />
                                </div>
                                <div className="row g-3 mb-3">
                                    <div className="col-md-8">
                                        <label className="form-label">Assessment</label>
                                        <textarea className="form-control" rows={2} value={form.assessment} onChange={(e) => setForm(f => ({ ...f, assessment: e.target.value }))} />
                                    </div>
                                    <div className="col-md-4">
                                        <label className="form-label">Diagnosis</label>
                                        <input type="text" className="form-control" value={form.diagnosis} onChange={(e) => setForm(f => ({ ...f, diagnosis: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Plan</label>
                                    <textarea className="form-control" rows={2} value={form.plan} onChange={(e) => setForm(f => ({ ...f, plan: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Medications Prescribed</label>
                                    <textarea className="form-control" rows={2} value={form.medications_prescribed} onChange={(e) => setForm(f => ({ ...f, medications_prescribed: e.target.value }))} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Follow-up Instructions</label>
                                    <textarea className="form-control" rows={2} value={form.follow_up_instructions} onChange={(e) => setForm(f => ({ ...f, follow_up_instructions: e.target.value }))} />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setShowCreate(false)}>Cancel</button>
                                <button className="btn btn-primary" disabled={submitting} onClick={submitRecord}>{submitting ? 'Saving...' : 'Save Record'}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {viewing && (
                <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{viewing.patient.name} — {new Date(viewing.visit_date).toLocaleString()}</h5>
                                <button type="button" className="btn-close" onClick={() => setViewing(null)}></button>
                            </div>
                            <div className="modal-body">
                                <span className={`badge mb-3 ${viewing.status === 'finalized' ? 'bg-success' : 'bg-warning'}`}>
                                    {viewing.status === 'finalized' && <FontAwesomeIcon icon={faLock} className="me-1" />}{viewing.status}
                                </span>
                                {Object.keys(viewing.vitals || {}).length > 0 && (
                                    <div className="mb-3 d-flex gap-2 flex-wrap">
                                        {Object.entries(viewing.vitals).map(([k, v]) => <span key={k} className="badge bg-light text-dark">{SPECIALTY_FIELD_LABELS[k] || k.toUpperCase()}: {String(v)}</span>)}
                                    </div>
                                )}
                                <p><strong>Chief Complaint:</strong> {viewing.chief_complaint}</p>
                                {viewing.history_of_present_illness && <p><strong>HPI:</strong> {viewing.history_of_present_illness}</p>}
                                {viewing.physical_examination && <p><strong>Physical Exam:</strong> {viewing.physical_examination}</p>}
                                <p><strong>Assessment:</strong> {viewing.assessment}</p>
                                {viewing.diagnosis && <p><strong>Diagnosis:</strong> {viewing.diagnosis}</p>}
                                <p><strong>Plan:</strong> {viewing.plan}</p>
                                {viewing.medications_prescribed && <p><strong>Medications:</strong> {viewing.medications_prescribed}</p>}
                                {viewing.follow_up_instructions && <p><strong>Follow-up:</strong> {viewing.follow_up_instructions}</p>}
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-light" onClick={() => setViewing(null)}>Close</button>
                                {viewing.status !== 'finalized' && (
                                    <button className="btn btn-success" onClick={finalizeRecord}>Finalize Record</button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </ProviderShell>
    );
};

export default ProviderRecords;
